/**
 * 订单号录入解析 · 纯逻辑(2026-06-24 从 order-no-metric-form-dialog 抽出,零行为改动)
 *
 * stage="order" 解析"订单号 + 时间";其他 stage 只识别订单号。
 * 系统按粘贴时空格/换行/逗号拆分,自动去重 + 跨子账号合并。
 */

// "hand" 保留在类型里:老数据仍有 stage="hand" 的 OrderNoRecord(举手占比 bar 回退时读);新录入不再产生
export type OrderStage = "hand" | "order" | "deal"
export const ORDER_STAGE_LABELS: Record<OrderStage, string> = { hand: "举手", order: "进单", deal: "成单" }

// 订单号识别规则:纯数字 ≥16 位(携程 vBooking 完整号目前 16 位,实测全库均 16;用 ≥ 不写死,防携程未来加长)。
// 不足 16 位 = 没输满 / 漏位 → 不识别(不计入派生数字、不入库)。
const ORDER_NO_PATTERN = /^\d{16,}$/
// 疑似"打错的订单号":长得像订单号但不是合法号(合法 = 纯数字 ≥16)。两类:
//   ① 纯数字 13-15 位(漏位、明显没打完)
//   ② 13-20 长度内、≥12 位数字但混了字母/符号(如 "112814...s61" 打错一位)
// 真·乱字符(短词、数字太少)静默忽略不提示。录入步停手 ~0.4s 才提示(防抖避免边打误报)。
export const isMalformedOrderNo = (t: string) =>
  t.length >= 13 && t.length <= 20 && (t.match(/\d/g)?.length ?? 0) >= 12 && !/^\d{16,}$/.test(t)
// 时分识别(可选秒): "9:23" / "09:23" / "09:23:45" — 仅 stage="order" 解析
const TIME_HM_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
// 完整日期时间识别: "2026-05-13 09:23" / "2026-05-13 09:23:45" — stage="order" 优先
const DATETIME_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})[\sT]+(\d{1,2}):(\d{2})(?::(\d{2}))?$/

function normalizeFullWidthAscii(raw: string) {
  return raw
    .replace(/\u3000/g, " ")
    .replace(/[！-～]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
}

export function normalizeOrderNoInput(raw: string) {
  return normalizeFullWidthAscii(raw)
    .replace(/\r\n?/g, "\n")
    .replace(/(\d{4})年(\d{1,2})月(\d{1,2})日?/g, "$1-$2-$3")
    .replace(/(\d{4})\/(\d{1,2})\/(\d{1,2})/g, "$1-$2-$3")
    .replace(/[，、；;,|｜]/g, " ")
    .replace(/[。·•]/g, " ")
    .replace(/[（）()[\]{}【】<>《》]/g, " ")
    .replace(/[\u3400-\u9fff]+/g, " ")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
}

// stage="order" 解析:每行识别"订单号 + 时间";其他 stage 只识别订单号
// metricDate 用于把 HH:MM 短时间合并成完整 DateTime ISO 字符串(YYYY-MM-DDTHH:MM:SS)
export interface ParsedOrderEntry {
  orderNo: string
  occurredAt: string | null // ISO 字符串或 null;仅 withTime=true 解析
}

export function parseOrderNos(
  raw: string,
  withTime: boolean,
  metricDate: string,
): {
  ok: ParsedOrderEntry[]
  ignored: string[]
  missingTime: number // 仅 withTime=true 时统计:订单号录了但没识别到时间
} {
  const seenOrderNos = new Set<string>()
  const ok: ParsedOrderEntry[] = []
  const ignored: string[] = []
  let missingTime = 0

  // 拆每行(用户可能多行,每行 1 个订单号 + 1 个时间)
  raw.split(/\r?\n/).forEach((line) => {
    const tokens = line.split(/[\s,;\t]+/).map((s) => s.trim()).filter(Boolean)
    if (tokens.length === 0) return
    let orderNo: string | null = null
    let occurredAt: string | null = null
    tokens.forEach((token) => {
      if (orderNo === null && ORDER_NO_PATTERN.test(token)) {
        orderNo = token
        return
      }
      if (withTime && occurredAt === null) {
        // 完整日期时间
        const dtMatch = DATETIME_PATTERN.exec(token)
        if (dtMatch) {
          const [, y, m, d, h, mm, s] = dtMatch
          occurredAt = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T${h.padStart(2, "0")}:${mm}:${s ?? "00"}+08:00`
          return
        }
        // 时分 → 合并 metricDate
        const hmMatch = TIME_HM_PATTERN.exec(token)
        if (hmMatch) {
          const [, h, mm, s] = hmMatch
          occurredAt = `${metricDate}T${h.padStart(2, "0")}:${mm}:${s ?? "00"}+08:00`
          return
        }
      }
      // 既不是订单号也不是时间 → 忽略
      ignored.push(token)
    })
    if (orderNo) {
      const orderNoStr = orderNo as string
      if (!seenOrderNos.has(orderNoStr)) {
        seenOrderNos.add(orderNoStr)
        ok.push({ orderNo: orderNoStr, occurredAt: withTime ? occurredAt : null })
        if (withTime && occurredAt === null) missingTime++
      }
    }
  })

  return { ok, ignored, missingTime }
}

export type ParseResult = { ok: ParsedOrderEntry[]; ignored: string[]; missingTime: number }
export type BucketArrays = Record<string, Record<OrderStage, ParseResult>>

// 录入派生计算(2026-06-24 从弹窗抽出,零行为改动):跳过/缺时间/重复/孤儿/malformed + 录入步问题列表
export function computeOrderNoDerived(
  bucketKeys: string[],
  bucketArrays: BucketArrays,
  dbExisting: { orders: string[]; deals: string[] },
) {
  const totals: Record<OrderStage, number> = { hand: 0, order: 0, deal: 0 }
  const overlapCounts: Record<OrderStage, number> = { hand: 0, order: 0, deal: 0 }
  const overlapNos: Record<OrderStage, string[]> = { hand: [], order: [], deal: [] }
  const missingTimeCount: Record<OrderStage, number> = { hand: 0, order: 0, deal: 0 }
  const dbOrders = new Set(dbExisting.orders)
  const dbDeals = new Set(dbExisting.deals)
  // 进单跳过:该号库里已成交 / 已录过进单 → 不计数 / 不算重复 / 不算缺时间 / 不入库(静默跳过 + 软提示)
  // 分两类,提示给具体原因(别用"或"):① 已成交(在 deals)② 已录过进单(在 orders)。已成交优先(更靠后阶段)
  const orderSkipped = new Set<string>() // 全部跳过(给 buildRecords)
  const orderSkippedDealt = new Set<string>() // 原因:已成交
  const orderSkippedOrdered = new Set<string>() // 原因:已录过进单(重复进单)
  const missingTimeNos = new Set<string>() // 进单缺时间的实际号(录入步显号用)
  {
    const seenOnce = new Set<string>()
    const overlapped = new Set<string>()
    bucketKeys.forEach((bk) => {
      bucketArrays[bk].order.ok.forEach((entry) => {
        if (dbDeals.has(entry.orderNo)) {
          orderSkipped.add(entry.orderNo)
          orderSkippedDealt.add(entry.orderNo)
          return
        }
        if (dbOrders.has(entry.orderNo)) {
          orderSkipped.add(entry.orderNo)
          orderSkippedOrdered.add(entry.orderNo)
          return
        }
        if (seenOnce.has(entry.orderNo)) overlapped.add(entry.orderNo)
        else seenOnce.add(entry.orderNo)
        if (entry.occurredAt === null) missingTimeNos.add(entry.orderNo)
      })
    })
    totals.order = seenOnce.size
    overlapCounts.order = overlapped.size
    overlapNos.order = Array.from(overlapped)
    missingTimeCount.order = missingTimeNos.size
  }
  // 本次输入的进单号(判断成单有无对应进单用)
  const enteredOrderNos = new Set<string>()
  bucketKeys.forEach((bk) => bucketArrays[bk].order.ok.forEach((e) => enteredOrderNos.add(e.orderNo)))
  // 成单跳过两类(都跳过不入库 + 提示):① 库里已成交过(重复成交)② 无对应进单(库里/本次都没进单 → 孤儿)
  const dealSkipped = new Set<string>()
  const dealNoOrder = new Set<string>()
  {
    const seenOnce = new Set<string>()
    const overlapped = new Set<string>()
    bucketKeys.forEach((bk) => {
      bucketArrays[bk].deal.ok.forEach((entry) => {
        if (dbDeals.has(entry.orderNo)) {
          dealSkipped.add(entry.orderNo)
          return
        }
        if (!dbOrders.has(entry.orderNo) && !enteredOrderNos.has(entry.orderNo)) {
          dealNoOrder.add(entry.orderNo)
          return
        }
        if (seenOnce.has(entry.orderNo)) overlapped.add(entry.orderNo)
        else seenOnce.add(entry.orderNo)
      })
    })
    totals.deal = seenOnce.size
    overlapCounts.deal = overlapped.size
    overlapNos.deal = Array.from(overlapped)
  }
  // 录入步「一号一行、按输入顺序」问题列表(type → 文案在 render 映射;同号去重)。汇总步仍用上面的计数集合
  const orderIssues: { no: string; type: "dealt" | "ordered" | "notime" }[] = []
  {
    const seen = new Set<string>()
    bucketKeys.forEach((bk) =>
      bucketArrays[bk].order.ok.forEach((e) => {
        if (seen.has(e.orderNo)) return
        if (orderSkippedDealt.has(e.orderNo)) { seen.add(e.orderNo); orderIssues.push({ no: e.orderNo, type: "dealt" }) }
        else if (orderSkippedOrdered.has(e.orderNo)) { seen.add(e.orderNo); orderIssues.push({ no: e.orderNo, type: "ordered" }) }
        else if (missingTimeNos.has(e.orderNo)) { seen.add(e.orderNo); orderIssues.push({ no: e.orderNo, type: "notime" }) }
      }),
    )
  }
  const dealIssues: { no: string; type: "dealt" | "noorder" }[] = []
  {
    const seen = new Set<string>()
    bucketKeys.forEach((bk) =>
      bucketArrays[bk].deal.ok.forEach((e) => {
        if (seen.has(e.orderNo)) return
        if (dealSkipped.has(e.orderNo)) { seen.add(e.orderNo); dealIssues.push({ no: e.orderNo, type: "dealt" }) }
        else if (dealNoOrder.has(e.orderNo)) { seen.add(e.orderNo); dealIssues.push({ no: e.orderNo, type: "noorder" }) }
      }),
    )
  }
  // 疑似打错的订单号(13-19 位非 16)——从被忽略的 token 里捞,录入步停手后提示(防静默丢真单)
  const malformedOrder: string[] = []
  const malformedDeal: string[] = []
  {
    const so = new Set<string>()
    const sd = new Set<string>()
    bucketKeys.forEach((bk) => {
      bucketArrays[bk].order.ignored.forEach((t) => {
        if (isMalformedOrderNo(t) && !so.has(t)) { so.add(t); malformedOrder.push(t) }
      })
      bucketArrays[bk].deal.ignored.forEach((t) => {
        if (isMalformedOrderNo(t) && !sd.has(t)) { sd.add(t); malformedDeal.push(t) }
      })
    })
  }
  return { totals, overlapCounts, overlapNos, missingTimeCount, orderSkipped, orderSkippedDealt, orderSkippedOrdered, missingTimeNos, dealSkipped, dealNoOrder, orderIssues, dealIssues, malformedOrder, malformedDeal }
}
